/*
 * New PDP description/specs tabs (sections/new-pdp-main.liquid).
 *
 * <new-pdp-tabs> wraps .new-pdp-tabs__panel elements (one per info block) and
 * builds the horizontal tab bar from their data-tab-title. Panels containing a
 * .new-pdp-tabs__probe element (the Specifications panel — toggled by
 * product-dimensions.js via its accordion id) get their tab hidden while the
 * probe is display:none, re-checked after every variant change.
 */
if (!customElements.get('new-pdp-tabs')) {
  let uid = 0;
  customElements.define(
    'new-pdp-tabs',
    class NewPdpTabs extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.new-pdp-tabs__bar');
        this.panels = Array.from(this.querySelectorAll('.new-pdp-tabs__panel'));

        if (!this.panels.length) {
          this.hidden = true;
          return;
        }
        if (!this.bar || this.buttons) return;

        const base = `new-pdp-tabs-${(uid += 1)}`;

        this.buttons = this.panels.map((panel, index) => {
          const tabId = `${base}-tab-${index}`;
          const panelId = panel.id || `${base}-panel-${index}`;
          panel.id = panelId;
          panel.setAttribute('aria-labelledby', tabId);

          const button = document.createElement('button');
          button.type = 'button';
          button.id = tabId;
          button.className = 'new-pdp-tabs__tab';
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-selected', 'false');
          button.setAttribute('aria-controls', panelId);
          // Roving tabindex — only the selected tab is a tab stop
          button.setAttribute('tabindex', '-1');
          button.textContent = panel.dataset.tabTitle || '';
          button.addEventListener('click', () => this.activate(index));
          button.addEventListener('keydown', (event) => this.onKeydown(event, index));
          this.bar.appendChild(button);
          return button;
        });

        this.refreshAvailability();

        // product-dimensions.js re-renders on both events; defer a tick so
        // the probe's display state is settled before re-checking
        document.addEventListener('variant_changed', () =>
          window.setTimeout(() => this.refreshAvailability(), 0)
        );
        document.addEventListener('DOMContentLoaded', () =>
          window.setTimeout(() => this.refreshAvailability(), 0)
        );
      }

      panelAvailable(panel) {
        const probe = panel.querySelector('.new-pdp-tabs__probe');
        return !probe || probe.style.display !== 'none';
      }

      refreshAvailability() {
        // Must read focus before hiding — a hidden tab has already lost it
        const focusedBefore = document.activeElement;

        this.panels.forEach((panel, index) => {
          this.buttons[index].hidden = !this.panelAvailable(panel);
        });

        const focusHidden = this.buttons.includes(focusedBefore) && focusedBefore.hidden;
        const firstVisible = this.buttons.findIndex((button) => !button.hidden);
        const activeIndex = this.buttons.findIndex((button) =>
          button.classList.contains('is-active')
        );

        if (activeIndex === -1 || this.buttons[activeIndex].hidden) {
          if (firstVisible !== -1) this.activate(firstVisible, focusHidden);
        } else if (focusHidden && this.buttons[activeIndex]) {
          // The focused inactive tab vanished; keep focus in the tab bar
          this.buttons[activeIndex].focus();
        }
      }

      // Arrow/Home/End among the VISIBLE tabs; moving focus also activates
      onKeydown(event, index) {
        const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 }[event.key];
        const visible = this.buttons.filter((button) => !button.hidden);
        if (!visible.length) return;

        let target;
        if (event.key === 'Home') {
          target = visible[0];
        } else if (event.key === 'End') {
          target = visible[visible.length - 1];
        } else if (step) {
          const pos = visible.indexOf(this.buttons[index]);
          target = visible[(pos + step + visible.length) % visible.length];
        } else {
          return;
        }

        event.preventDefault();
        this.activate(this.buttons.indexOf(target), true);
      }

      activate(index, focus) {
        this.panels.forEach((panel, panelIndex) => {
          const selected = panelIndex === index;
          panel.hidden = !selected;
          const button = this.buttons[panelIndex];
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
          button.setAttribute('tabindex', selected ? '0' : '-1');
        });
        // Keyboard nav only — refreshAvailability()'s call must not pull focus
        // out of the buy stack on every variant change
        if (focus && this.buttons[index]) this.buttons[index].focus();
      }
    }
  );
}